import React from 'react';
import { SchematicShell, Rect, Line } from './SchematicPrimitives';
import {
  STORAGE,
  STORAGE_SHARED,
  classifyStorage,
  clampSpec,
  dresserDrawerCount,
  bookshelfShelfCount,
  consoleDoorCount,
  wallShelfPanelThickness,
  wallShelfBays,
  nightstandDrawerCount,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematic for `storage`.
 *
 * RoomPlan tags many casegoods as one `storage` category. This schematic calls
 * `classifyStorage(dims, obj.storageSubType)` — the SAME classifier the 3D
 * `storageRenderer` uses — and dispatches to one of FIVE sub-layouts, mirroring
 * `storageRenderers.js` (proportions single-sourced in
 * `proceduralRenderers/proceduralSpecs.js` → STORAGE):
 *
 *   wardrobe   — 2 full-height doors with vertical pulls
 *   bookshelf  — open horizontal shelves, no doors
 *   dresser    — 3-4 stacked drawers with horizontal pulls
 *   console    — 2-3 wide doors with vertical pulls
 *   nightstand — 1-2 small drawers with horizontal pulls
 *
 * Every sub-type floats its body on thin metal legs (legHeightFrac per spec).
 * Front + side views; right view mirrors left. The dispatched sub-type is
 * exposed on the root group as `data-storage-subtype` so the sync test can
 * assert the schematic dispatched to the same sub-type `classifyStorage`
 * returns.
 */

const legHeight = (spec, height) =>
  clampSpec(height * spec.legHeightFrac, spec.legHeightMin, spec.legHeightMax);
const legInset = (spec, width) =>
  clampSpec(width * spec.legInsetFrac, spec.legInsetMin, spec.legInsetMax);

/** Two thin metal legs that reach the floor (y = height) at the corners. */
function Legs({ spec, width, height, bodyBottomY, strokeColor, strokeWidth }) {
  const inset = legInset(spec, width);
  const r = spec.legRadius;
  return (
    <>
      {[inset, width - inset].map((cx, i) => (
        <Rect
          key={`leg-${i}`}
          x={cx - r}
          y={bodyBottomY}
          width={2 * r}
          height={height - bodyBottomY}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
    </>
  );
}

export function StorageSchematic({
  obj,
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const sub = classifyStorage({ width, height, depth }, obj?.storageSubType, {
    // Footprint cannot tell a mounted unit from a standing one; post-processing
    // stamps the mount and the 3D renderer reads it. Dropping this argument is
    // what drew metal legs under a unit bolted to the wall.
    mount: obj?.storageMount,
  });
  const isSide = view === 'left' || view === 'right';
  const viewportW = isSide ? depth : width;

  const inner = isSide ? (
    <SideProfile
      sub={sub}
      depth={depth}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  ) : (
    <FrontLayout
      sub={sub}
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );

  return (
    <SchematicShell
      width={viewportW}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <g
        data-storage-subtype={sub}
        transform={
          isSide && view === 'right'
            ? `scale(-1, 1) translate(${-viewportW}, 0)`
            : ''
        }
      >
        {inner}
      </g>
    </SchematicShell>
  );
}

// ---------------------------------------------------------------------------
// Front sub-layouts
// ---------------------------------------------------------------------------

function FrontLayout({ sub, width, height, strokeColor, strokeWidth }) {
  const builders = {
    'wall-shelf': WallShelfFront,
    wardrobe: WardrobeFront,
    bookshelf: BookshelfFront,
    dresser: DresserFront,
    console: ConsoleFront,
    nightstand: NightstandFront,
  };
  const Builder = builders[sub] || DresserFront;
  return (
    <Builder
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

const GAP = STORAGE_SHARED.gap;
const BOARD = STORAGE_SHARED.boardThick;

function CarcassAndLegs({ spec, width, height, strokeColor, strokeWidth }) {
  const legH = legHeight(spec, height);
  const bodyBottomY = height - legH;
  return {
    legH,
    bodyBottomY,
    elements: (
      <>
        <Rect
          x={0}
          y={0}
          width={width}
          height={bodyBottomY}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Legs
          spec={spec}
          width={width}
          height={height}
          bodyBottomY={bodyBottomY}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    ),
  };
}

/** Thin vertical pull near a door's inner edge. */
function DoorPull({ x, doorTopY, doorH, strokeColor, strokeWidth }) {
  const pullH = clampSpec(doorH * 0.4, 0.1, 0.35);
  const pullW = STORAGE_SHARED.pullThick * 1.6;
  return (
    <Rect
      x={x - pullW / 2}
      y={doorTopY + doorH / 2 - pullH / 2}
      width={pullW}
      height={pullH}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      fill={strokeColor}
    />
  );
}

/** Thin horizontal pull centered on a drawer front. */
function DrawerPull({ cx, cy, frontW, strokeColor, strokeWidth }) {
  const pullW = clampSpec(frontW * 0.45, 0.08, 0.4);
  const pullH = STORAGE_SHARED.pullThick * 1.6;
  return (
    <Rect
      x={cx - pullW / 2}
      y={cy - pullH / 2}
      width={pullW}
      height={pullH}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      fill={strokeColor}
    />
  );
}

function WardrobeFront({ width, height, strokeColor, strokeWidth }) {
  const spec = STORAGE.wardrobe;
  const { bodyBottomY, elements } = CarcassAndLegs({
    spec,
    width,
    height,
    strokeColor,
    strokeWidth,
  });
  const doorTopY = GAP;
  const doorH = bodyBottomY - 2 * GAP;
  const doorW = width / 2 - GAP * 1.5;
  return (
    <g>
      {elements}
      {/* Center divider between the two doors. */}
      <Line
        x1={width / 2}
        y1={0}
        x2={width / 2}
        y2={bodyBottomY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {[-1, 1].map((side, i) => {
        const doorCx = width / 2 + side * (width / 4);
        const doorX = doorCx - doorW / 2;
        const pullX = width / 2 + side * GAP * 1.5;
        return (
          <g key={`door-${i}`}>
            <Rect
              x={doorX}
              y={doorTopY}
              width={doorW}
              height={doorH}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
            <DoorPull
              x={pullX}
              doorTopY={doorTopY}
              doorH={doorH}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
          </g>
        );
      })}
    </g>
  );
}

/**
 * WALL-SHELF front — mounted, so there are NO legs and the carcass runs the
 * full height. Boards are drawn at the same pitch the 3D `buildWallShelf` uses
 * (`wallShelfBays`), so the two views can never show a different shelf count
 * for the same unit.
 */
function WallShelfFront({ width, height, strokeColor, strokeWidth }) {
  const panel = wallShelfPanelThickness(width); // side panels read on the width axis
  const bays = wallShelfBays(height);
  return (
    <g>
      {/* Full-height carcass — no leg gap beneath it. */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Side panel inner edges. */}
      {[panel, width - panel].map((x, i) => (
        <Line
          key={`side-${i}`}
          x1={x}
          y1={0}
          x2={x}
          y2={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
      {/* Interior boards. `bays` bays means bays-1 interior shelves; the top
          and bottom boards are already the carcass outline. */}
      {Array.from({ length: Math.max(0, bays - 1) }, (_, k) => {
        const sy = (height * (k + 1)) / bays;
        return (
          <Line
            key={`shelf-${k}`}
            x1={panel}
            y1={sy}
            x2={width - panel}
            y2={sy}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
    </g>
  );
}

function BookshelfFront({ width, height, strokeColor, strokeWidth }) {
  const spec = STORAGE.bookshelf;
  const legH = legHeight(spec, height);
  const bodyBottomY = height - legH;
  const bodyH = bodyBottomY;
  const innerTop = BOARD;
  const innerBottom = bodyBottomY - BOARD;
  const innerH = innerBottom - innerTop;
  const shelfCount = bookshelfShelfCount(innerH);
  return (
    <g>
      {/* Open carcass frame (sides + top + bottom boards drawn as one outline). */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={bodyH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Side panel inner edges. */}
      <Line
        x1={BOARD}
        y1={0}
        x2={BOARD}
        y2={bodyH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={width - BOARD}
        y1={0}
        x2={width - BOARD}
        y2={bodyH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Open horizontal shelves, evenly spaced. */}
      {Array.from({ length: shelfCount }, (_, k) => {
        const sy = innerTop + (innerH * (k + 1)) / (shelfCount + 1);
        return (
          <Line
            key={`shelf-${k}`}
            x1={BOARD}
            y1={sy}
            x2={width - BOARD}
            y2={sy}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
      <Legs
        spec={spec}
        width={width}
        height={height}
        bodyBottomY={bodyBottomY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function StackedDrawersFront({
  spec,
  width,
  height,
  count,
  strokeColor,
  strokeWidth,
}) {
  const legH = legHeight(spec, height);
  const bodyBottomY = height - legH;
  const bodyH = bodyBottomY;
  const drawerW = width - 2 * GAP;
  const drawerH = (bodyH - GAP * (count + 1)) / count;
  return (
    <g>
      <Rect
        x={0}
        y={0}
        width={width}
        height={bodyH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {Array.from({ length: count }, (_, k) => {
        // Drawer 0 is the bottom drawer (mirrors the renderer's index order).
        const drawerTopY = bodyBottomY - (GAP + drawerH + k * (drawerH + GAP));
        const cy = drawerTopY + drawerH / 2;
        return (
          <g key={`drawer-${k}`}>
            <Rect
              x={GAP}
              y={drawerTopY}
              width={drawerW}
              height={drawerH}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
            <DrawerPull
              cx={width / 2}
              cy={cy}
              frontW={drawerW}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
          </g>
        );
      })}
      <Legs
        spec={spec}
        width={width}
        height={height}
        bodyBottomY={bodyBottomY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function DresserFront({ width, height, strokeColor, strokeWidth }) {
  const spec = STORAGE.dresser;
  const legH = legHeight(spec, height);
  const bodyH = height - legH;
  const count = dresserDrawerCount(bodyH);
  return (
    <StackedDrawersFront
      spec={spec}
      width={width}
      height={height}
      count={count}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

function NightstandFront({ width, height, strokeColor, strokeWidth }) {
  const spec = STORAGE.nightstand;
  const legH = legHeight(spec, height);
  const bodyH = height - legH;
  const count = nightstandDrawerCount(bodyH);
  return (
    <StackedDrawersFront
      spec={spec}
      width={width}
      height={height}
      count={count}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

function ConsoleFront({ width, height, strokeColor, strokeWidth }) {
  const spec = STORAGE.console;
  const legH = legHeight(spec, height);
  const bodyBottomY = height - legH;
  const bodyH = bodyBottomY;
  const count = consoleDoorCount(width);
  const doorH = bodyH - 2 * GAP;
  const doorW = (width - GAP * (count + 1)) / count;
  const doorTopY = GAP;
  return (
    <g>
      <Rect
        x={0}
        y={0}
        width={width}
        height={bodyH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {Array.from({ length: count }, (_, k) => {
        const doorX = GAP + k * (doorW + GAP);
        const doorCx = doorX + doorW / 2;
        // Pull toward the cabinet-center side of each door.
        const pullX =
          doorCx + (doorCx <= width / 2 ? doorW / 2 - 0.03 : -doorW / 2 + 0.03);
        return (
          <g key={`door-${k}`}>
            <Rect
              x={doorX}
              y={doorTopY}
              width={doorW}
              height={doorH}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
            <DoorPull
              x={pullX}
              doorTopY={doorTopY}
              doorH={doorH}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
          </g>
        );
      })}
      <Legs
        spec={spec}
        width={width}
        height={height}
        bodyBottomY={bodyBottomY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Side profile — body carcass on legs; bookshelves show shelf lines.
// ---------------------------------------------------------------------------

function SideProfile({ sub, depth, height, strokeColor, strokeWidth }) {
  // Mounted: full-height carcass with its board edges, and no legs at all.
  if (sub === 'wall-shelf') {
    const bays = wallShelfBays(height);
    return (
      <g>
        <Rect
          x={0}
          y={0}
          width={depth}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
        {Array.from({ length: Math.max(0, bays - 1) }, (_, k) => (
          <Line
            key={`side-shelf-${k}`}
            x1={0}
            y1={(height * (k + 1)) / bays}
            x2={depth}
            y2={(height * (k + 1)) / bays}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        ))}
      </g>
    );
  }

  const spec = STORAGE[sub] || STORAGE.dresser;
  const legH = legHeight(spec, height);
  const bodyBottomY = height - legH;
  const inset = legInset(spec, depth);
  const r = spec.legRadius;

  return (
    <g>
      {/* Body side silhouette. */}
      <Rect
        x={0}
        y={0}
        width={depth}
        height={bodyBottomY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Open bookshelf: shelf edges visible from the side. */}
      {sub === 'bookshelf' &&
        (() => {
          const innerTop = BOARD;
          const innerBottom = bodyBottomY - BOARD;
          const innerH = innerBottom - innerTop;
          const shelfCount = bookshelfShelfCount(innerH);
          return Array.from({ length: shelfCount }, (_, k) => {
            const sy = innerTop + (innerH * (k + 1)) / (shelfCount + 1);
            return (
              <Line
                key={`side-shelf-${k}`}
                x1={0}
                y1={sy}
                x2={depth}
                y2={sy}
                strokeColor={strokeColor}
                strokeWidth={strokeWidth}
              />
            );
          });
        })()}
      {/* Front/back legs reaching the floor. */}
      {[inset, depth - inset].map((cx, i) => (
        <Rect
          key={`side-leg-${i}`}
          x={cx - r}
          y={bodyBottomY}
          width={2 * r}
          height={height - bodyBottomY}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
    </g>
  );
}

StorageSchematic.supportsSideViews = true;
